import { useQuery } from "@tanstack/react-query";
import { useMemo, useEffect, useRef } from "react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { Product } from "@/types/product";
import { useProductQueryCache } from "./use-product-query-cache";
import { useProductEvents } from "./use-product-events";

interface ParsedQuery {
  entity: string[];
  materials: string[];
  brand: string | null;
  remainder: string[];
  raw: string;
}

export interface QueryChange {
  from: string;
  to: string;
  type: "alias" | "typo" | "spacing";
}

interface ProductSearchResponse {
  products: Product[];
  totalCount: number;
  hasMore: boolean;
  limit: number;
  offset: number;
  correctedQuery?: string;
  queryChanges?: QueryChange[];
  parsed: ParsedQuery | null;
  /** Whether the server actually applied LLM re-ranking to these results. */
  llmReranked?: boolean;
  /** The model used for re-ranking, when it was applied. */
  rerankModel?: string;
  /** Which retrieval engine served these results ("elastic" | "postgres"). */
  engine?: "elastic" | "postgres";
  /**
   * Id of the search that produced this result, generated once per queryFn
   * run and cached alongside the rest of the response. Reading it off
   * `query.data` (rather than a ref that only advances on a fresh fetch)
   * means a cache hit for query A correctly returns A's id even if a
   * different query B was fetched in between.
   */
  searchId: string;
}

interface ProductSearchOptions {
  searchQuery: string;
  limit?: number;
  offset?: number;
  enabled?: boolean;
  inStockOnly?: boolean;
  skipCorrection?: boolean;
  /** Experimental: re-rank the top candidates with an LLM and promote its top picks. */
  llmRerank?: boolean;
  /**
   * Which LLM rerank variant to request when `llmRerank` is true. "lto" targets the
   * progressive List flow (fast model); defaults to "search".
   */
  rerankVariant?: "search" | "lto";
  eventName?: "search_products" | "list_search_products";
}

/**
 * Search products using the backend search service
 * This hook provides server-side fuzzy search with advanced features like
 * measurement pattern matching and multi-token search
 */
export function useProductSearch({
  searchQuery,
  limit = 50,
  offset = 0,
  enabled = true,
  inStockOnly = false,
  skipCorrection = false,
  llmRerank = false,
  rerankVariant = "search",
  eventName = "search_products",
}: ProductSearchOptions) {
  // Only search if query is at least 2 characters
  const shouldSearch = searchQuery.trim().length >= 2;
  // Elasticsearch and Postgres FTS are two entirely separate routes — the
  // client decides which one to call, from its own PostHog flag read. There
  // is no server-side dispatch: `search-engine` true means hit
  // /api/products/search-elasticsearch, otherwise /api/products/search.
  const useElastic = useFeatureFlagEnabled("search-engine") ?? false;
  const { queryKeys, addToCache } = useProductQueryCache();
  const { capture } = useProductEvents({ component: "useProductSearch" });
  const searchStartTimeRef = useRef<number | null>(null);
  const queryFnRanRef = useRef<boolean>(false);
  const lastEmittedSearchQueryRef = useRef<string>("");
  // Remember the last re-rank model seen so consumers (e.g. the loading state)
  // can reflect the active provider even while a new search is still in flight
  // and this query's own data hasn't arrived yet. Updated after `query` below.
  const lastRerankModelRef = useRef<string | undefined>(undefined);
  // Same idea for the retrieval engine, so the loading animation stays on the
  // right variant while a new search is in flight.
  const lastEngineRef = useRef<"elastic" | "postgres" | undefined>(undefined);

  // Construct API query parameters
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();

    if (shouldSearch && searchQuery.trim()) {
      params.append("search", searchQuery.trim());
    }

    params.append("limit", limit.toString());
    params.append("offset", offset.toString());

    if (inStockOnly) {
      params.append("inStockOnly", "true");
    }

    if (skipCorrection) {
      params.append("skipCorrection", "true");
    }

    // Layer experimental LLM re-ranking on top of the search.
    if (llmRerank) {
      params.append("llmRerank", "true");
      if (rerankVariant !== "search") {
        params.append("rerankVariant", rerankVariant);
      }
    }

    return params;
  }, [searchQuery, limit, offset, shouldSearch, inStockOnly, skipCorrection, llmRerank, rerankVariant]);

  // Use React Query for caching and state management
  const query = useQuery({
    queryKey: queryKeys.search(
      searchQuery.trim(),
      limit,
      offset,
      inStockOnly,
      skipCorrection,
      llmRerank,
      rerankVariant,
      useElastic ? "elastic" : "postgres",
    ),
    queryFn: async (): Promise<ProductSearchResponse> => {
      // Mark that queryFn ran (not from cache)
      queryFnRanRef.current = true;

      // Generate search ID and track start time for event tracking
      const searchId = `search_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      searchStartTimeRef.current = Date.now();

      // Non-search listing always uses /api/products. For search queries,
      // the client itself picks Elasticsearch vs Postgres FTS — two
      // completely separate routes, no server-side flag involved.
      const endpoint = !shouldSearch
        ? "/api/products"
        : useElastic
          ? "/api/products/search-elasticsearch"
          : "/api/products/search";
      const url = `${endpoint}?${queryParams.toString()}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Search request failed: ${response.statusText}`);
      }

      const data = await response.json();

      // V3 search endpoint returns { products: [...], parsed: {...}, correctedQuery?: string }
      if (data && typeof data === "object" && "products" in data && Array.isArray(data.products)) {
        if (data.products.length > 0) {
          addToCache(queryKeys.initial(), data.products);
        }
        return {
          products: data.products,
          totalCount: data.products.length,
          hasMore: data.products.length === limit,
          limit,
          offset,
          correctedQuery: data.correctedQuery ?? undefined,
          queryChanges: data.queryChanges ?? undefined,
          parsed: data.parsed ?? null,
          llmReranked: data.llmReranked === true,
          rerankModel: data.rerankModel ?? undefined,
          engine: data.engine ?? undefined,
          searchId,
        };
      }

      // Non-search endpoint still returns a flat array
      if (Array.isArray(data)) {
        if (data.length > 0) {
          addToCache(queryKeys.initial(), data);
        }
        return {
          products: data,
          totalCount: data.length,
          hasMore: data.length === limit,
          limit,
          offset,
          parsed: null,
          searchId,
        };
      }

      return {
        products: [],
        totalCount: 0,
        hasMore: false,
        limit,
        offset,
        parsed: null,
        searchId,
      };
    },
    enabled: enabled,
    staleTime: shouldSearch ? 30000 : 300000, // 30s for search, 5min for all products
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false,
  });

  // Emit search_products event when search completes successfully (both fresh and cached)
  useEffect(() => {
    if (!(query.data && shouldSearch)) {
      return;
    }
    const currentQuery = searchQuery.trim();
    const isNewSearch =
      lastEmittedSearchQueryRef.current !== currentQuery &&
      currentQuery.length >= 2;
    if (!isNewSearch) {
      return;
    }

    const fromCache = !queryFnRanRef.current;

    // Calculate latency - use 0 for cache hits, actual time for fresh fetches
    const latencyMs = fromCache
      ? 0
      : searchStartTimeRef.current
        ? Date.now() - searchStartTimeRef.current
        : 0;

    const productIds = query.data.products.map((p) => p.uid);

    capture({
      event: eventName,
      context: {
        productIds,
        query: currentQuery,
        latencyMs: latencyMs,
        resultCount: query.data.totalCount,
        searchId: query.data.searchId,
        fromCache: fromCache,
        llmReranked: query.data.llmReranked ?? false,
        rerankModel: query.data.rerankModel ?? null,
        parsedEntity: query.data.parsed?.entity ?? [],
        parsedMaterials: query.data.parsed?.materials ?? [],
        parsedBrand: query.data.parsed?.brand ?? null,
        parsedRemainder: query.data.parsed?.remainder ?? [],
      },
    });

    if (query.data.totalCount === 0) {
      capture({
        event: "search_no_results",
        context: {
          query: currentQuery,
          searchId: query.data.searchId,
          latencyMs,
        },
      });
    }

    // Update tracking refs
    lastEmittedSearchQueryRef.current = currentQuery;
    searchStartTimeRef.current = null;
    queryFnRanRef.current = false; // Reset for next check
  }, [query.data, searchQuery, shouldSearch, capture]);

  if (query.data?.rerankModel) {
    lastRerankModelRef.current = query.data.rerankModel;
  }
  if (query.data?.engine) {
    lastEngineRef.current = query.data.engine;
  }

  return {
    products: query.data?.products || [],
    totalCount: query.data?.totalCount || 0,
    hasMore: query.data?.hasMore || false,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetched: query.isFetched,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    searchId: query.data?.searchId ?? "",
    correctedQuery: query.data?.correctedQuery,
    queryChanges: query.data?.queryChanges,
    // Current result's model when present, else the last one seen (so the
    // loading state can reflect the active provider mid-search).
    rerankModel: query.data?.rerankModel ?? lastRerankModelRef.current,
    // Likewise the active engine, so the loading animation variant is stable
    // across in-flight refetches.
    engine: query.data?.engine ?? lastEngineRef.current,
  };
}
